"use client";

import { LOCAL_STORAGE_KEY } from "@/components/hooks/use-local-storage";
import { type ReactNode, useEffect, useState } from "react";
import { CookieConsentContext } from "./use-cookie-consent";

const STORAGE_KEY = LOCAL_STORAGE_KEY.cookieConsent;

type CookieConsent = "granted" | "denied" | null;

/**
 * 初回マウント時にlocalStorageから同意状態を読み取る。
 *
 * Safari private mode やストレージアクセス制限でlocalStorageが例外を投げる場合がある。
 * 同意未設定として扱い、UIは表示状態を維持する（バナーが出続けるだけで害はない）。
 */
function readInitialConsent(): CookieConsent {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    if (value === "granted" || value === "denied") {
      return value;
    }
  } catch {
    // noop
  }
  return null;
}

/**
 * Cookie同意状態のContext Provider。
 *
 * 同意状態は `useState` で保持し、`grant`/`deny` 呼び出しで更新する。
 * localStorageには永続化として同時に書き込むが、UI状態の正となるのはstate。
 * → 保存失敗（Safari private等）でもUI動作は維持される。
 *
 * 本Providerはトラッキング等の副作用を持たず、consentのstate管理と他タブ同期のみを担う。
 * 同意取得UI（CookieConsentBanner）は利用側(layout)で明示配置する。
 * トラッキング側（UserTrackingProvider）は `useCookieConsent()` 経由でconsentを購読し、
 * 変化に応じてtrackerへ同期する（依存方向: tracking → cookie-consent）。
 */
export function CookieConsentProvider({
  available,
  children,
}: {
  available: boolean;
  children: ReactNode;
}) {
  const [consent, setConsent] = useState<CookieConsent>(readInitialConsent);

  // 他タブでのlocalStorage変更を検知してstateに反映する（cross-tab同期）。
  // storageイベントは他タブでのlocalStorage変更時にのみ発火する仕様で、
  // 同一タブ内での自分の変更はsetConsentによるstate更新で直接反映されるため不要。
  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) {
        return;
      }
      if (e.newValue === "granted" || e.newValue === "denied") {
        setConsent(e.newValue);
      } else {
        // 他タブでremoveItem/clearされた場合は初期状態に戻す
        setConsent(null);
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  const persistAndSet = (next: "granted" | "denied") => {
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // noop
    }
    setConsent(next);
  };

  return (
    <CookieConsentContext.Provider
      value={{
        available,
        consent,
        grant: () => persistAndSet("granted"),
        deny: () => persistAndSet("denied"),
      }}
    >
      {children}
    </CookieConsentContext.Provider>
  );
}
